import React from "react";

// Order Info band shown in place of the Front / Rear cover + clamp fields when
// the customer bought one pair only (front_cover_code FF = no front · rear RR = no rear).
export function SideNotOrderedCard({ side, order, onRestore }) {
  const d = order?.shopify_discounts;
  const viaDiscount = (order?.cover_sides_source || "").startsWith("shopify_discount");
  const other = side === "front" ? "rear" : "front";
  return (
    <div className="rounded-md border border-dashed border-slate-300 bg-slate-50 p-3 flex flex-col justify-between" data-testid={`${side}-not-ordered`}>
      <div>
        <div className="text-[11px] uppercase tracking-widest font-mono text-slate-500">{side === "front" ? "Front" : "Rear"} covers · not ordered</div>
        <div className="text-sm text-slate-700 mt-1">
          Customer bought the <b>{other.toUpperCase()} pair only</b>
          {viaDiscount && d?.label ? <> — Shopify discount <span className="font-mono font-semibold">🏷 {d.label}</span></> : null}.
          No {side} cover code, clamp or {side} photos are required.
        </div>
      </div>
      {onRestore && (
        <button type="button" onClick={onRestore} className="self-start mt-2 text-[11px] underline text-slate-500 hover:text-slate-800" data-testid={`${side}-not-ordered-restore`}>
          Customer does want {side} covers → re-enable
        </button>
      )}
    </div>
  );
}

// Shopify tab block: every discount on the order + the single-side rule it triggered.
export function ShopifyDiscountCard({ order }) {
  const d = order?.shopify_discounts;
  if (!d && !order?.cover_sides) return null;
  const codes = d?.codes || [];
  const apps = (d?.applications || []).filter((a) => a.type !== "discount_code");
  const fmt = (v) => (v === null || v === undefined || v === "" ? "—" : v);
  return (
    <div className="mt-5 rounded-md border border-amber-200 bg-amber-50/60 p-4" data-testid="shopify-discounts">
      <div className="text-[11px] uppercase tracking-widest font-mono text-amber-800">🏷 Discounts on this order</div>
      {codes.length === 0 && apps.length === 0 && <div className="text-sm text-slate-600 mt-1">Total discount {fmt(d?.total)} {d?.currency || ""}</div>}
      <div className="mt-2 space-y-1">
        {codes.map((c, i) => (
          <div key={`c${i}`} className="flex flex-wrap items-center gap-3 text-sm" data-testid={`shopify-discount-code-${i}`}>
            <span className="font-mono font-semibold text-slate-900">{c.code}</span>
            <span className="text-slate-600">{c.type === "percentage" ? `${c.amount}%` : `−${c.amount} ${d?.currency || ""}`}</span>
            <span className="text-[11px] text-slate-500">discount code</span>
          </div>
        ))}
        {apps.map((a, i) => (
          <div key={`a${i}`} className="flex flex-wrap items-center gap-3 text-sm" data-testid={`shopify-discount-app-${i}`}>
            <span className="font-semibold text-slate-900">{a.title || a.code || a.description || "Discount"}</span>
            <span className="text-slate-600">{a.value_type === "percentage" ? `${a.value}%` : `−${fmt(a.value)} ${d?.currency || ""}`}</span>
            <span className="text-[11px] text-slate-500">{a.type === "automatic" ? "automatic discount" : a.type || ""}</span>
          </div>
        ))}
      </div>
      <div className="text-xs text-slate-600 mt-2">Total discount: <b>{fmt(d?.total)} {d?.currency || ""}</b></div>
      {order?.cover_sides && (
        <div className="mt-3 rounded border border-blue-300 bg-blue-50 px-3 py-2 text-sm text-blue-900" data-testid="shopify-discount-side-rule">
          ↳ <b>{order.cover_sides.toUpperCase()} PAIR ONLY</b> — the CRM marked the {order.cover_sides === "rear" ? "front" : "rear"} covers as not ordered
          ({order.cover_sides === "rear" ? "FF" : "RR"}): no {order.cover_sides === "rear" ? "front" : "rear"} codes, clamps or photos will be requested.
        </div>
      )}
    </div>
  );
}

// Compact list marker: 🏷 CODE · REAR ONLY
export function DiscountChip({ order, className = "" }) {
  const d = order?.shopify_discounts;
  const side = order?.cover_sides;
  if (!d && !side) return null;
  const label = d?.label;
  return (
    <span className={`inline-flex items-center gap-1 rounded border border-amber-300 bg-amber-50 px-1.5 py-0.5 text-[10px] font-mono font-semibold text-amber-900 ${className}`}
      title={[label ? `Discount ${label}` : null, d?.total ? `−${d.total} ${d.currency || ""}` : null, side ? `${side} pair only` : null].filter(Boolean).join(" · ")}
      data-testid={`discount-chip-${order.id}`}>
      🏷 {label || "DISCOUNT"}{side ? ` · ${side.toUpperCase()} ONLY` : ""}
    </span>
  );
}
